import { z } from 'zod';

/**
 * Zod schemas mirroring the backend validation rules for evaluation and
 * feedback module details. Used by the editors to gate autosave so we never
 * push an incomplete payload that the backend would reject.
 *
 * Keep these in sync with backend/app/api/schemas/course_schemas.py.
 */

const TEXT = z.string().min(1).max(2000);

const optionDef = z.object({
  id: z.string().min(1),
  text: TEXT,
});

const pairDef = z.object({
  id: z.string().min(1),
  left: TEXT,
  right: TEXT,
});

const blankDef = z.object({
  id: z.string().min(1),
  answer: TEXT,
});

const baseItemFields = {
  id: z.string().min(1),
  order_index: z.number().int().min(0),
  points: z.number().int().min(0),
};

const evaluationItem = z.discriminatedUnion('type', [
  z.object({
    ...baseItemFields,
    type: z.literal('multiple_choice'),
    config: z.object({
      question: TEXT,
      options: z.array(optionDef).min(2),
      correct_answers: z.array(z.string().min(1)).min(1),
    }),
  }),
  z.object({
    ...baseItemFields,
    type: z.literal('single_choice'),
    config: z.object({
      question: TEXT,
      options: z.array(optionDef).min(2),
      correct_answer: z.string().min(1),
    }),
  }),
  z.object({
    ...baseItemFields,
    type: z.literal('short_answer_text'),
    config: z.object({
      question: TEXT,
      expected_answer: TEXT,
      case_sensitive: z.boolean().optional(),
    }),
  }),
  z.object({
    ...baseItemFields,
    type: z.literal('completion_fill_blank'),
    config: z.object({
      prompt: TEXT,
      blanks: z.array(blankDef).min(1),
    }),
  }),
  z.object({
    ...baseItemFields,
    type: z.literal('free_text_essay'),
    config: z.object({
      prompt: TEXT,
      min_words: z.number().int().min(0).optional(),
      max_words: z.number().int().min(1).optional(),
      sample_answer: z.string().optional(),
    }),
  }),
  z.object({
    ...baseItemFields,
    type: z.literal('ordering_sequence'),
    config: z.object({
      question: TEXT,
      items: z.array(optionDef).min(2),
    }),
  }),
  z.object({
    ...baseItemFields,
    type: z.literal('tuple_matching'),
    config: z.object({
      question: TEXT,
      pairs: z.array(pairDef).min(2),
    }),
  }),
  z.object({
    ...baseItemFields,
    type: z.literal('numeric_input'),
    config: z.object({
      question: TEXT,
      correct_value: z.number(),
      tolerance: z.number().min(0).optional(),
    }),
  }),
  z.object({
    ...baseItemFields,
    type: z.literal('boolean_true_false'),
    config: z.object({
      question: TEXT,
      correct_value: z.boolean(),
    }),
  }),
  z.object({
    ...baseItemFields,
    type: z.literal('code_submission'),
    config: z.object({
      prompt: TEXT,
      language: z.string().min(1),
      starter_code: z.string().optional(),
      expected_output: z.string().optional(),
    }),
  }),
]);

export const evaluationDetailsSchema = z.object({
  items: z.array(evaluationItem).min(1).max(100),
  passing_grade_percent: z.number().min(0).max(100).nullable(),
  max_attempts: z.number().int().min(1).nullable(),
  shuffle_items: z.boolean(),
  // z.object() strips unknown keys on parse — without this entry the
  // main-quiz flag would be silently dropped from every autosave.
  is_main_quiz: z.boolean().optional(),
});

const feedbackItem = z.discriminatedUnion('type', [
  z.object({
    id: z.string().min(1),
    order_index: z.number().int().min(0),
    required: z.boolean(),
    type: z.literal('free_input_feedback'),
    config: z.object({
      question: TEXT,
      max_length: z.number().int().min(1).optional(),
    }),
  }),
  z.object({
    id: z.string().min(1),
    order_index: z.number().int().min(0),
    required: z.boolean(),
    type: z.literal('likert_3_scale'),
    config: z.object({ question: TEXT }),
  }),
  z.object({
    id: z.string().min(1),
    order_index: z.number().int().min(0),
    required: z.boolean(),
    type: z.literal('likert_5_scale'),
    config: z.object({ question: TEXT }),
  }),
  z.object({
    id: z.string().min(1),
    order_index: z.number().int().min(0),
    required: z.boolean(),
    type: z.literal('likert_10_scale'),
    config: z.object({ question: TEXT }),
  }),
]);

export const feedbackDetailsSchema = z.object({
  items: z.array(feedbackItem).min(1).max(100),
  anonymous: z.boolean(),
});
